import React, { useEffect, useState } from "react";
import { PageShell, PageHero, PageSection } from "../components/PageShell";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import AdminNav from "../components/AdminNav";
import { Link } from "react-router-dom";
import { adminService } from "../services/adminService";
import FormModal from "../components/FormModal";
import { toast } from "sonner";
 

export default function AdminContactAdminPage() {
  const [features, setFeatures] = useState([]);
  const [channels, setChannels] = useState([]);
  const [error, setError] = useState("");

  const [addFeatureOpen, setAddFeatureOpen] = useState(false);
  const [editFeatureOpen, setEditFeatureOpen] = useState(false);
  const [featureEditing, setFeatureEditing] = useState(null);
  const [featureDraft, setFeatureDraft] = useState({ icon: "Activity", title: "", desc: "", order: 0, isPublished: true });

  const [addChannelOpen, setAddChannelOpen] = useState(false);
  const [editChannelOpen, setEditChannelOpen] = useState(false);
  const [channelEditing, setChannelEditing] = useState(null);
  const [channelDraft, setChannelDraft] = useState({ icon: "Mail", label: "", value: "", order: 0, isPublished: true });

  async function load() {
    setError("");
    const [fRes, cRes] = await Promise.all([
      adminService.listContactFeaturesAdmin(),
      adminService.listContactChannelsAdmin(),
    ]);
    if (!fRes.ok) return setError(fRes.error || "Failed to load contact features");
    if (!cRes.ok) return setError(cRes.error || "Failed to load contact channels");
    setFeatures(fRes.data?.items || []);
    setChannels(cRes.data?.items || []);
  }

  useEffect(() => { load(); }, []);

  return (
    <PageShell>
      <PageHero eyebrow="Admin" title="Contact" highlight="content." description="Edit contact content and review contact form submissions." />
      <PageSection variant="content">
        <div className="mx-auto max-w-7xl grid gap-6">
          <AdminNav />
          {error ? <Card><CardContent className="py-4 text-sm text-destructive">{error}</CardContent></Card> : null}

          <div className="flex gap-2 flex-wrap">
            <Button onClick={() => setAddFeatureOpen(true)}>Add feature</Button>
            <Button onClick={() => setAddChannelOpen(true)}>Add channel</Button>
            <Link to="/admin/contact-responses"><Button variant="outline">Open contact requests</Button></Link>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <Card className="shadow-soft md:col-span-2">
              <CardHeader><CardTitle>Contact features</CardTitle></CardHeader>
              <CardContent className="grid md:grid-cols-2 gap-4">
                {features.map((f) => (
                  <Card key={f._id} className="shadow-soft">
                    <CardHeader><CardTitle>{f.title}</CardTitle></CardHeader>
                    <CardContent className="text-sm text-muted-foreground space-y-3">
                      <div className="text-xs">icon: {f.icon}</div>
                      <div>{f.desc}</div>
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" onClick={() => { setFeatureEditing(f); setFeatureDraft({ icon: f.icon, title: f.title, desc: f.desc, order: f.order || 0, isPublished: f.isPublished !== false }); setEditFeatureOpen(true); }}>Edit</Button>
                        <Button size="sm" variant="outline" onClick={async () => { const r = await adminService.deleteContactFeature(f._id); if (!r.ok) return toast.error(r.error || "Delete failed"); toast.success("Feature deleted"); await load(); }}>Delete</Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </CardContent>
            </Card>

            <Card className="shadow-soft md:col-span-2">
              <CardHeader><CardTitle>Contact channels</CardTitle></CardHeader>
              <CardContent className="grid md:grid-cols-3 gap-4">
                {channels.map((ch) => (
                  <Card key={ch._id} className="shadow-soft">
                    <CardHeader><CardTitle className="flex items-center justify-between gap-3"><span>{ch.label}</span></CardTitle></CardHeader>
                    <CardContent className="text-sm text-muted-foreground space-y-3">
                      <div className="text-xs">icon: {ch.icon}</div>
                      <div>{ch.value}</div>
                      <div className="flex gap-2 flex-wrap">
                        <Button size="sm" variant="outline" onClick={() => { setChannelEditing(ch); setChannelDraft({ icon: ch.icon, label: ch.label, value: ch.value, order: ch.order || 0, isPublished: ch.isPublished !== false }); setEditChannelOpen(true); }}>Edit</Button>
                        <Button size="sm" variant="outline" onClick={async () => { const r = await adminService.deleteContactChannel(ch._id); if (!r.ok) return toast.error(r.error || "Delete failed"); toast.success("Channel deleted"); await load(); }}>Delete</Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </CardContent>
            </Card>

          </div>
        </div>
      </PageSection>

      <FormModal open={addFeatureOpen} title="Add Contact Feature" onClose={() => setAddFeatureOpen(false)}>
        <div className="grid gap-3">
          <input className="h-10 px-3 rounded-xl border border-border bg-background text-sm" placeholder="Icon name (Activity/BarChart3/Bell...)" value={featureDraft.icon} onChange={(e) => setFeatureDraft((p) => ({ ...p, icon: e.target.value }))} />
          <input className="h-10 px-3 rounded-xl border border-border bg-background text-sm" placeholder="Title" value={featureDraft.title} onChange={(e) => setFeatureDraft((p) => ({ ...p, title: e.target.value }))} />
          <textarea className="min-h-24 px-3 py-2 rounded-xl border border-border bg-background text-sm" placeholder="Description" value={featureDraft.desc} onChange={(e) => setFeatureDraft((p) => ({ ...p, desc: e.target.value }))} />
          <div className="flex gap-2">
            <Button size="sm" onClick={async () => {
              const r = await adminService.createContactFeature(featureDraft);
              if (!r.ok) return toast.error(r.error || "Create failed");
              toast.success("Feature added");
              setAddFeatureOpen(false);
              await load();
            }}>Save</Button>
            <Button size="sm" variant="outline" onClick={() => setAddFeatureOpen(false)}>Cancel</Button>
          </div>
        </div>
      </FormModal>

      <FormModal open={editFeatureOpen} title="Edit Contact Feature" onClose={() => setEditFeatureOpen(false)}>
        <div className="grid gap-3">
          <input className="h-10 px-3 rounded-xl border border-border bg-background text-sm" value={featureDraft.icon} onChange={(e) => setFeatureDraft((p) => ({ ...p, icon: e.target.value }))} />
          <input className="h-10 px-3 rounded-xl border border-border bg-background text-sm" value={featureDraft.title} onChange={(e) => setFeatureDraft((p) => ({ ...p, title: e.target.value }))} />
          <textarea className="min-h-24 px-3 py-2 rounded-xl border border-border bg-background text-sm" value={featureDraft.desc} onChange={(e) => setFeatureDraft((p) => ({ ...p, desc: e.target.value }))} />
          <div className="flex gap-2">
            <Button size="sm" onClick={async () => {
              if (!featureEditing?._id) return;
              const r = await adminService.updateContactFeature(featureEditing._id, featureDraft);
              if (!r.ok) return toast.error(r.error || "Update failed");
              toast.success("Feature updated");
              setEditFeatureOpen(false);
              await load();
            }}>Update</Button>
            <Button size="sm" variant="outline" onClick={() => setEditFeatureOpen(false)}>Cancel</Button>
          </div>
        </div>
      </FormModal>

      <FormModal open={addChannelOpen} title="Add Contact Channel" onClose={() => setAddChannelOpen(false)}>
        <div className="grid gap-3">
          <input className="h-10 px-3 rounded-xl border border-border bg-background text-sm" placeholder="Icon name (Mail/Phone/MapPin)" value={channelDraft.icon} onChange={(e) => setChannelDraft((p) => ({ ...p, icon: e.target.value }))} />
          <input className="h-10 px-3 rounded-xl border border-border bg-background text-sm" placeholder="Label" value={channelDraft.label} onChange={(e) => setChannelDraft((p) => ({ ...p, label: e.target.value }))} />
          <input className="h-10 px-3 rounded-xl border border-border bg-background text-sm" placeholder="Value" value={channelDraft.value} onChange={(e) => setChannelDraft((p) => ({ ...p, value: e.target.value }))} />
          <div className="flex gap-2">
            <Button size="sm" onClick={async () => {
              const r = await adminService.createContactChannel(channelDraft);
              if (!r.ok) return toast.error(r.error || "Create failed");
              toast.success("Channel added");
              setAddChannelOpen(false);
              await load();
            }}>Save</Button>
            <Button size="sm" variant="outline" onClick={() => setAddChannelOpen(false)}>Cancel</Button>
          </div>
        </div>
      </FormModal>

      <FormModal open={editChannelOpen} title="Edit Contact Channel" onClose={() => setEditChannelOpen(false)}>
        <div className="grid gap-3">
          <input className="h-10 px-3 rounded-xl border border-border bg-background text-sm" value={channelDraft.icon} onChange={(e) => setChannelDraft((p) => ({ ...p, icon: e.target.value }))} />
          <input className="h-10 px-3 rounded-xl border border-border bg-background text-sm" value={channelDraft.label} onChange={(e) => setChannelDraft((p) => ({ ...p, label: e.target.value }))} />
          <input className="h-10 px-3 rounded-xl border border-border bg-background text-sm" value={channelDraft.value} onChange={(e) => setChannelDraft((p) => ({ ...p, value: e.target.value }))} />
          <div className="flex gap-2">
            <Button size="sm" onClick={async () => {
              if (!channelEditing?._id) return;
              const r = await adminService.updateContactChannel(channelEditing._id, channelDraft);
              if (!r.ok) return toast.error(r.error || "Update failed");
              toast.success("Channel updated");
              setEditChannelOpen(false);
              await load();
            }}>Update</Button>
            <Button size="sm" variant="outline" onClick={() => setEditChannelOpen(false)}>Cancel</Button>
          </div>
        </div>
      </FormModal>
    </PageShell>
  );
}

